// src/components/my_journey/Achievements.jsx
import React, { useEffect, useRef } from "react";
import se1 from "../../assets/personal/summit_extra/se1.jpg";
import se2 from "../../assets/personal/summit_extra/se2.jpg";
import se3 from "../../assets/personal/summit_extra/se3.jpg";
import se4 from "../../assets/personal/summit_extra/se4.jpg";
import se5 from "../../assets/personal/summit_extra/se5.jpg";
import se6 from "../../assets/personal/summit_extra/se6.jpg";
import se7 from "../../assets/personal/summit_extra/se7.jpg";
import se8 from "../../assets/personal/summit_extra/se8.jpg";
import se9 from "../../assets/personal/summit_extra/se9.jpg";
import se10 from "../../assets/personal/summit_extra/se10.jpg";
import se11 from "../../assets/personal/summit_extra/se11.jpg";

import AchievementsIcon from "../../assets/my_journey_achievements_icon.png";

const summitExtraImages = [
  se1,
  se2,
  se3,
  se4,
  se5,
  se6,
  se7,
  se8,
  se9,
  se10,
  se11,
];

export default function Achievements() {
  const containerRef = useRef(null);
  const scrollRef = useRef(0);
  const animationRef = useRef(null);

  // Smooth auto scroll
  useEffect(() => {
    const scrollSpeed = 0.5; // pixels per frame
    const step = () => {
      if (containerRef.current) {
        scrollRef.current += scrollSpeed;
        const totalWidth = containerRef.current.scrollWidth / 2;
        if (scrollRef.current >= totalWidth) scrollRef.current = 0;
        containerRef.current.style.transform = `translateX(-${scrollRef.current}px)`;
      }
      animationRef.current = requestAnimationFrame(step);
    };
    animationRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationRef.current);
  }, []);

  const smoothScroll = (distance) => {
    if (!containerRef.current) return;
    const start = scrollRef.current;
    const duration = 400;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 0.5 - Math.cos(progress * Math.PI) / 2;
      scrollRef.current = start + distance * ease;
      const totalWidth = containerRef.current.scrollWidth / 2;
      if (scrollRef.current >= totalWidth) scrollRef.current -= totalWidth;
      if (scrollRef.current < 0) scrollRef.current += totalWidth;
      containerRef.current.style.transform = `translateX(-${scrollRef.current}px)`;
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  };

  const scrollLeft = () => smoothScroll(-containerRef.current.offsetWidth / 3);
  const scrollRight = () => smoothScroll(containerRef.current.offsetWidth / 3);

  return (
    <section className="py-16 px-4 md:px-8 flex flex-col items-center gap-5 bg-black/30 backdrop-blur-sm">
      {/* Separator Line */}
      <div className="w-full flex justify-center mb-8">
        <div className="h-1 bg-gray-500 rounded-full animate-growLine w-4/5 max-w-[90%]"></div>
      </div>

      {/* Heading */}
      <div className="flex items-center gap-4 w-full max-w-[90%] mb-8">
        <img
          src={AchievementsIcon}
          alt="Achievements Icon"
          className="w-11 h-11 md:w-14 md:h-14"
        />
        <h1 className="text-white text-4xl md:text-5xl font-bold underline decoration-gray-400 decoration-1 underline-offset-2">
          Achievements
        </h1>
      </div>

      {/* Summits Section */}
      <div className="mb-16 flex justify-center w-full">
        <div className="max-w-[90%]">
          <h2 className="text-neonTurquoise italic text-3xl md:text-4xl font-semibold mb-6 text-left animate-fadeInLeft">
            Summits
          </h2>
          <ul className=" text-gray-200 text-lg md:text-xl mb-6 list-disc list-inside">
            {[
              "Conquered iconic peaks including Grays Peak, Mt Bierstadt, Mt Democrat, Quandary Peak, Mt Sherman, and Mt Lincoln.",
              "Developed endurance, pacing, and mental resilience through multi-day climbs across challenging terrains.",
              "Documented each summit with photography and reflective notes to capture the personal journey.",
              "Inspired fellow enthusiasts to explore mountaineering while emphasizing preparation and safety.",
              "Focused on skill-building, self-awareness, and strategic planning to confidently overcome each climb.",
            ].map((item, index) => (
              <li
                key={index}
                className="transform transition-transform duration-300 hover:text-gray-400 animate-fadeInLeft"
                style={{
                  animationDelay: `${index * 200}ms`,
                  animationFillMode: "both",
                }}
              >
                {item}
              </li>
            ))}
          </ul>

          {/* Summits Carousel */}
          <div className="w-full relative overflow-hidden rounded-2xl shadow-lg bg-gradient-to-b from-black/10 to-black/20">
            <div
              ref={containerRef}
              className="flex flex-nowrap gap-4 transition-transform duration-500 ease-out"
              style={{ willChange: "transform" }}
            >
              {summitExtraImages.concat(summitExtraImages).map((img, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 w-1/2 sm:w-1/3 md:w-1/4 cursor-pointer transform transition-transform duration-300 hover:scale-105 hover:shadow-2xl"
                >
                  <img
                    src={img}
                    alt={`achievement-${i}`}
                    className="w-full h-72 sm:h-96 md:h-[32rem] object-cover rounded-lg"
                  />
                </div>
              ))}
            </div>

            {/* Arrows */}
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-0 h-full w-12 md:w-16 bg-gradient-to-r from-black/50 to-transparent text-white text-2xl flex items-center justify-center hover:from-black/70 transition z-10"
            >
              &lt;
            </button>
            <button
              onClick={scrollRight}
              className="absolute right-0 top-0 h-full w-12 md:w-16 bg-gradient-to-l from-black/50 to-transparent text-white text-2xl flex items-center justify-center hover:from-black/70 transition z-10"
            >
              &gt;
            </button>
          </div>
        </div>
      </div>

      {/* Marathon and 14er Section */}
      <div className="max-w-[90%] w-full flex flex-col md:flex-row gap-8">
        {/* Marathon Achievements */}
        <div className="md:w-1/2 bg-black/40 p-6 rounded-xl shadow-lg animate-fadeInLeft">
          <h2 className="text-neonTurquoise italic text-2xl md:text-3xl font-semibold mb-4">
            Marathon Achievements
          </h2>
          <p className="text-gray-200 text-lg md:text-xl leading-relaxed">
            Completing marathons has been a journey of endurance, focus, and
            self-discovery. Each race tested my limits, refined my pacing
            strategies, and strengthened mental fortitude. Training required
            consistent effort, diet discipline, and detailed planning to
            optimize performance.
          </p>
          <p className="text-gray-200 text-lg md:text-xl leading-relaxed mt-6">
            Crossing finish lines instilled a sense of accomplishment and
            humility, reinforcing the value of perseverance. These experiences
            have informed my approach to coaching, teaching others the
            importance of structured preparation, self-awareness, and
            maintaining balance. Beyond physical gains, marathons fostered
            resilience, adaptability, and a mindset ready to tackle challenges
            both on and off the track.
          </p>
        </div>

        {/* Dashed Line */}
        <div className="hidden md:block border-l-2 border-dashed border-gray-500"></div>

        {/* 14er Achievements */}
        <div className="md:w-1/2 bg-black/40 p-6 rounded-xl shadow-lg animate-fadeInRight">
          <h2 className="text-neonTurquoise italic text-2xl md:text-3xl font-semibold mb-4">
            14er Achievements
          </h2>
          <p className="text-gray-200 text-lg md:text-xl leading-relaxed">
            Summiting 14,000-foot peaks has been both physically demanding and
            mentally enriching. Each climb required careful acclimatization,
            technical skill, and strategic pacing. The high-altitude environment
            taught lessons in resilience, awareness, and patience. Tracking
            progress, mapping routes, and mastering safety protocols ensured
            each ascent was successful.{" "}
          </p>
          <p className="text-gray-200 text-lg md:text-xl leading-relaxed mt-6">
            These mountains inspired reflection, gratitude, and determination,
            shaping both character and leadership. Sharing these achievements
            motivates others to set ambitious goals, embrace challenges, and
            prioritize preparation. The combination of physical training, mental
            strategy, and environmental appreciation creates a holistic
            experience that informs my approach to life and fitness coaching.
          </p>
        </div>
      </div>
    </section>
  );
}
